import { useState, useEffect, useCallback } from 'react';
import { socialAPI } from '@/lib/api';
import PageHeader from '@/components/Layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Megaphone, Image as ImageIcon, Plus, Trash, PushPin, ArrowsClockwise
} from '@phosphor-icons/react';

export default function AnnouncementsPage() {
  const [tab, setTab] = useState('announcements');
  const [announcements, setAnnouncements] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addAnnDialog, setAddAnnDialog] = useState(false);
  const [addPhotoDialog, setAddPhotoDialog] = useState(false);
  const [annForm, setAnnForm] = useState({ title: '', body: '', type: 'announcement', is_pinned: false });
  const [photoTitle, setPhotoTitle] = useState('');
  const [photoDesc, setPhotoDesc] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, gRes] = await Promise.all([
        socialAPI.getAnnouncements(),
        socialAPI.getGallery(),
      ]);
      setAnnouncements(aRes.data.announcements || []);
      setGallery(gRes.data.photos || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createAnnouncement = async () => {
    try {
      await socialAPI.createAnnouncement(annForm);
      toast.success('Announcement posted');
      setAddAnnDialog(false);
      setAnnForm({ title: '', body: '', type: 'announcement', is_pinned: false });
      load();
    } catch { toast.error('Failed'); }
  };

  const deleteAnnouncement = async (id) => {
    try {
      await socialAPI.deleteAnnouncement(id);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', photoTitle);
      fd.append('description', photoDesc);
      fd.append('photo', photoFile);
      await socialAPI.uploadGalleryPhoto(fd);
      toast.success('Photo uploaded');
      setAddPhotoDialog(false);
      setPhotoTitle(''); setPhotoDesc(''); setPhotoFile(null);
      load();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const deletePhoto = async (id) => {
    try {
      await socialAPI.deleteGalleryPhoto(id);
      toast.success('Photo deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <PageHeader title="Announcements & Gallery" subtitle="Manage notices, updates and company photos for the Employee Portal" />

      <div className="flex gap-2">
        <Button variant={tab === 'announcements' ? 'default' : 'outline'} onClick={() => setTab('announcements')} data-testid="tab-announcements">
          <Megaphone size={14} className="mr-1.5" /> Announcements ({announcements.length})
        </Button>
        <Button variant={tab === 'gallery' ? 'default' : 'outline'} onClick={() => setTab('gallery')} data-testid="tab-gallery">
          <ImageIcon size={14} className="mr-1.5" /> Photo Gallery ({gallery.length})
        </Button>
        <Button variant="outline" size="sm" onClick={load} className="ml-auto">
          <ArrowsClockwise size={14} />
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#64748B]">Loading...</div>
      ) : tab === 'announcements' ? (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setAddAnnDialog(true)} data-testid="new-announcement-btn">
              <Plus size={14} className="mr-1" /> New Announcement
            </Button>
          </div>
          {announcements.length === 0 ? (
            <div className="text-center py-12 text-[#64748B] bg-white border border-[#E2E8F0] rounded-lg">No announcements yet</div>
          ) : (
            <div className="space-y-3">
              {announcements.map(a => (
                <div key={a.id} className={`bg-white border rounded-lg p-4 flex items-start gap-4 ${a.is_pinned ? 'border-amber-300 bg-amber-50/50' : 'border-[#E2E8F0]'}`} data-testid={`admin-ann-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {a.is_pinned && <PushPin size={14} className="text-amber-500" weight="fill" />}
                      <h4 className="font-semibold text-sm text-[#0F4C81]">{a.title}</h4>
                      <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded uppercase">{a.type}</span>
                    </div>
                    <p className="text-sm text-[#64748B] mt-1 whitespace-pre-line">{a.body}</p>
                    <p className="text-[10px] text-[#9CA3AF] mt-2">Posted: {new Date(a.created_at).toLocaleString('en-IN')}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteAnnouncement(a.id)} className="text-red-500 hover:bg-red-50 flex-shrink-0" data-testid={`delete-ann-${a.id}`}>
                    <Trash size={16} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setAddPhotoDialog(true)} data-testid="new-photo-btn">
              <Plus size={14} className="mr-1" /> Upload Photos
            </Button>
          </div>
          {gallery.length === 0 ? (
            <div className="text-center py-12 text-[#64748B] bg-white border border-[#E2E8F0] rounded-lg">No photos yet</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map(p => (
                <div key={p.id} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden group" data-testid={`admin-photo-${p.id}`}>
                  <div className="aspect-square relative">
                    <img src={`${API_URL}${p.url}`} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                    <button onClick={() => deletePhoto(p.id)} className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${p.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium truncate">{p.title}</p>
                    <p className="text-[10px] text-[#9CA3AF]">{new Date(p.created_at).toLocaleDateString('en-IN')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New Announcement Dialog */}
      <Dialog open={addAnnDialog} onOpenChange={setAddAnnDialog}>
        <DialogContent className="max-w-md" data-testid="new-announcement-dialog">
          <DialogHeader><DialogTitle>New Announcement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={annForm.title} onChange={(e) => setAnnForm(f => ({...f, title: e.target.value}))} data-testid="ann-title-input" />
            <Textarea placeholder="Message body..." rows={4} value={annForm.body} onChange={(e) => setAnnForm(f => ({...f, body: e.target.value}))} data-testid="ann-body-input" />
            <div className="flex gap-3">
              <Select value={annForm.type} onValueChange={(v) => setAnnForm(f => ({...f, type: v}))}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="announcement">Announcement</SelectItem>
                  <SelectItem value="notice">Notice</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={annForm.is_pinned} onChange={(e) => setAnnForm(f => ({...f, is_pinned: e.target.checked}))} />
                Pin to top
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddAnnDialog(false)}>Cancel</Button>
            <Button onClick={createAnnouncement} disabled={!annForm.title || !annForm.body} data-testid="post-announcement-btn">Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Photo Dialog */}
      <Dialog open={addPhotoDialog} onOpenChange={setAddPhotoDialog}>
        <DialogContent className="max-w-md" data-testid="upload-photo-dialog">
          <DialogHeader><DialogTitle>Upload Photo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title (optional)" value={photoTitle} onChange={(e) => setPhotoTitle(e.target.value)} data-testid="photo-title-input" />
            <Input placeholder="Description (optional)" value={photoDesc} onChange={(e) => setPhotoDesc(e.target.value)} />
            <Input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} data-testid="photo-file-input" />
            {photoFile && <p className="text-xs text-green-600">{photoFile.name} ({(photoFile.size / 1024).toFixed(0)} KB)</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPhotoDialog(false)}>Cancel</Button>
            <Button onClick={uploadPhoto} disabled={!photoFile || uploading} data-testid="upload-photo-btn">{uploading ? 'Uploading...' : 'Upload'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
